import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Label,
  Input,
} from 'reactstrap';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import ActionButton from '../../../../Components/Common/ActionButton';
import CrudeInfoTab from './CrudeInfoTab';
import EvaluationTab from './EvaluationTab';
import ImplementationTab, { toFirstOfMonth } from './ImplementationTab';
import {
  createAddDetailDraft,
  updateAddDetailDraft,
  submitAddDetailDraft,
  createUpdateDetailDraft,
  updateUpdateDetailDraft,
  getUpdateDetailDraft,
  submitUpdateDetailDraft,
  uploadDraftDocument,
  deleteDraftDocument,
} from './registrationApi';
import {
  validateCrudeInfo,
  validateImplementation,
  mapBackendErrors,
} from './registrationValidation';
import {
  computeTotalYield,
  deriveYieldClassName,
  deriveSulfurClassName,
  yieldLabelToName,
} from './classification';
import mapDetailToForm from './mapDetailToForm';
import { formatForAPI } from '../../../../utils/dateformatpickr';

// Add / Edit Assay editor for the Crude Registration Detail page
// (rule-crude-registration.md §Detail 8.1 Edit / §9 Add Assay). Reuses the three
// Create-wizard tabs (Crude Info / Evaluation / Implementation) and drives the
// STAGED Draft flows §AA1–AA6 (add) / §UA1–UA6 (edit) — the same per-tab draft
// pattern as the Create wizard (RegistrationCreateView.js), not the old
// single-shot AddDetail/UpdateDetail bundle.
//
// FLOW (mirrors RegistrationCreateView):
//   Tab 1 Next → createAddDetailDraft/createUpdateDetailDraft (AA1/UA1) the first
//     time (stores crudeDetailId), else update the draft (AA2/UA2). In edit mode
//     the server auto-clones the assay's existing documents into staging, so we
//     read them back via getUpdateDetailDraft (UA3) to hydrate documentsByEval.
//   Tab 2/3 → documents are staged locally as 'pending' rows and batch-flushed to
//     the shared Draft staging endpoint (uploadDraftDocument, §AA4/UA4) at the tab
//     boundary; deletes go through deleteDraftDocument (§AA5/UA5) for saved rows.
//   Submit → flush any pending docs, then submitAddDetailDraft/submitUpdateDetail
//     Draft (AA6/UA6) with { crudeDetailId, implementations } — no masaBerlaku/
//     notes override (those were finalized in AA1/UA1).
//
// ENUMS: the Draft endpoints are [FromBody] JSON and accept string member names,
// so classification* go as STRINGS (deriveYieldClassName/deriveSulfurClassName /
// yieldLabelToName) — unlike the single-shot §4/§5 endpoints which required ints.

const TAB_CONFIG = [
  { id: 1, icon: 'ri-flask-line', label: 'ASSAY & YIELD' },
  { id: 2, icon: 'ri-file-list-3-line', label: 'EVALUATION' },
  { id: 3, icon: 'ri-settings-3-line', label: 'IMPLEMENTATION' },
];

// Same Tab-1 keys as the wizard, used to route a backend 400 field error to an
// inline highlight and bounce the user back to Tab 1.
const CRUDE_INFO_FIELDS = [
  'crudeName',
  'origin',
  'country',
  'codeG5',
  'productionRate',
  'parcelSize',
  'penyaluran',
  'assaYear',
  'lab',
  'api',
  'sulfur',
  'pourPoint',
  'tan',
  'asphaltene',
  'sg',
  'salt',
  'sw',
  'mercury',
  'gas',
  'lNaphta',
  'hNaphta',
  'kerosene',
  'lGasoil',
  'hGasoil',
  'residu',
  'totalYield',
];

const num = v => (v === '' || v === null || v === undefined ? null : Number(v));

// Hydrate documentsByEval (keyed by evaluationId) from a Detail-draft's
// pendingDocuments[] (§UA3 — includes the auto-cloned existing docs). Each becomes
// a 'saved' row carrying its pendingDocumentId, matching the wizard's shape so the
// shared tabs behave identically (delete goes through deleteDraftDocument).
// NOTE: pendingDocuments[] field casing is assumed to mirror D4's response
// (pendingDocumentId) + the doc entry fields (evaluationId/fileName/refDocument/
// effDate) — adjust here if the live UA3 response differs.
const hydrateDocumentsFromDraft = pendingDocuments => {
  const grouped = {};
  (pendingDocuments || []).forEach(doc => {
    const key = doc.evaluationId;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push({
      localId: `saved-${doc.pendingDocumentId}`,
      status: 'saved',
      pendingDocumentId: doc.pendingDocumentId,
      fileName: doc.fileName,
      refDocument: doc.refDocument,
      effDate: doc.effDate,
    });
  });
  return grouped;
};

// Only CDU rows with a touched group are sent; each technical*/implementation*
// group is all-or-nothing (validated before submit). Mirrors the wizard.
const buildImplementations = form => {
  const rows = Object.values(form.implementations || {});
  return rows
    .filter(
      r =>
        r.technicalStatus !== '' ||
        r.implementationStatus !== '' ||
        r.technicalRatio ||
        r.implementationRatio
    )
    .map(r => ({
      cduId: r.cduId,
      technicalStatus:
        r.technicalStatus === '' || r.technicalStatus === undefined
          ? null
          : r.technicalStatus === 'true',
      technicalRatio: num(r.technicalRatio),
      technicalLimit: num(r.technicalLimit),
      implementationStatus: num(r.implementationStatus),
      implementationRatio: num(r.implementationRatio),
      implementationLimit: num(r.implementationLimit),
    }));
};

// Build the AA1/AA2 (add) or UA1/UA2 (edit) draft payload from the flat form.
// STRING enums (Draft endpoints deserialize with JsonStringEnumConverter). NO
// documents/implementations — docs stage separately, implementations go at Submit.
// Both notes live inline here because AA6/UA6 have no notes override:
// description ⇄ form.implementationNote (Tab-3), evaluationNote ⇄ form.evaluationNote
// (Tab-2) — per the project's Assay notes-field mapping.
const buildAssayDraftPayload = (form, mode, crudeId, detailId) => {
  const totalYield = computeTotalYield(form);
  const base = {
    crudeId,
    assaYear: num(form.assaYear),
    lab: form.lab,
    api: num(form.api),
    sulfur: num(form.sulfur),
    pourPoint: num(form.pourPoint),
    tan: num(form.tan),
    asphaltene: num(form.asphaltene),
    sg: num(form.sg),
    salt: num(form.salt),
    sw: num(form.sw),
    mercury: num(form.mercury),
    classificationYield: deriveYieldClassName(
      totalYield,
      form.gas,
      form.hNaphta
    ),
    classificationSulfur: deriveSulfurClassName(form.sulfur),
    description: form.implementationNote || null,
    evaluationNote: form.evaluationNote || null,
    gas: num(form.gas),
    lNaphta: num(form.lNaphta),
    hNaphta: num(form.hNaphta),
    kerosene: num(form.kerosene),
    lGasoil: num(form.lGasoil),
    hGasoil: num(form.hGasoil),
    residu: num(form.residu),
    totalYield,
    masaBerlaku: form.masaBerlaku
      ? formatForAPI(toFirstOfMonth(form.masaBerlaku))
      : null,
    isLatUpdate: !!form.isLatUpdate,
  };
  if (mode === 'edit') {
    // id = the CrudeDetail being edited; classificationYieldBefore as a string
    // member name (seeded from GetCrude as a label, member name, or int).
    // Falls back to the freshly-derived classificationYield when it resolves
    // to null (e.g. no prior classification stored) so the payload never
    // submits a null classificationYieldBefore.
    return {
      ...base,
      id: detailId,
      classificationYieldBefore:
        yieldLabelToName(form.classificationYieldBefore) ??
        base.classificationYield,
    };
  }
  return base;
};

const AssayEditorSection = ({
  mode,
  crude,
  initialDetail,
  onClose,
  onSaved,
}) => {
  const [activeTab, setActiveTab] = useState(1);
  const [form, setForm] = useState(() =>
    mode === 'edit'
      ? mapDetailToForm(initialDetail, crude)
      : {
          // Add mode: seed the General header from the parent crude (read-only
          // context) so Tab 1 isn't blank, but leave assay/yield empty.
          crudeName: crude?.crudeName ?? '',
          origin: crude?.origin ?? '',
          country: crude?.country ?? '',
          codeG5: crude?.codeG5 ?? '',
          productionRate: crude?.productionRate ?? '',
          parcelSize: crude?.parcelSize ?? '',
          penyaluran: crude?.penyaluran ?? '',
          isBlending: (crude?.compositions || []).length > 0,
          compositions: (crude?.compositions || []).map(comp => ({
            crudeText: comp.crudeText,
            composition: comp.composition,
          })),
          isLatUpdate: false,
          implementations: {},
        }
  );
  const [errors, setErrors] = useState({});
  const [implErrors, setImplErrors] = useState({});

  // The Detail-draft id (crudeDetailId) returned by AA1/UA1 — needed by every
  // subsequent doc upload/delete and by Submit. Null until Tab-1 Next.
  const [crudeDetailId, setCrudeDetailId] = useState(null);

  // Documents grouped by evaluationId (Tab 2 & 3). 'pending' rows are staged
  // locally (raw file, no BE call); 'saved' rows have been flushed to the Draft
  // staging endpoint and carry a pendingDocumentId. Same shape as the wizard.
  const [documentsByEval, setDocumentsByEval] = useState({});

  const [savingDraft, setSavingDraft] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const goTo = tab => setActiveTab(tab);

  const warnToast = msg =>
    toast.error(msg, {
      position: 'top-right',
      className: 'bg-warning text-black',
    });

  // Same field-error routing as the wizard shell (handleApiError): a backend 400
  // with a per-field `validationErrors` map → inline Tab-1 highlight + bounce to
  // Tab 1. Everything else (incl. 409 duplicate/concurrent-draft, arriving as a
  // plain string) falls through to the prefixed fallback toast.
  const handleApiError = (error, fallbackPrefix) => {
    const validationErrors = error?.validationErrors;
    if (validationErrors) {
      const { fields, messages } = mapBackendErrors(
        validationErrors,
        CRUDE_INFO_FIELDS
      );
      if (Object.keys(fields).length > 0) {
        setErrors(fields);
        goTo(1);
      }
      warnToast(messages[0] || String(error));
      return;
    }
    toast.error(`${fallbackPrefix}: ${error}`);
  };

  // Tab 1 Next — validate, then create (AA1/UA1) the draft the first time or
  // update it (AA2/UA2) on a return visit. Edit mode hydrates the doc list from
  // the server-cloned staging docs (UA3) right after creating the draft.
  const handleNextFromCrudeInfo = async () => {
    const { errors: errorList, fields } = validateCrudeInfo(form, {
      // AssayEditorSection is only ever rendered from the Detail page (both
      // Add and Edit) — General (crude header) is never shown there, so it's
      // never validated here either.
      skipGeneral: true,
    });
    setErrors(fields);
    if (errorList.length > 0) {
      warnToast(errorList[0]);
      return;
    }

    setSavingDraft(true);
    try {
      const payload = buildAssayDraftPayload(
        form,
        mode,
        crude?.id,
        initialDetail?.id
      );
      if (!crudeDetailId) {
        const create =
          mode === 'edit' ? createUpdateDetailDraft : createAddDetailDraft;
        const res = await create(payload);
        const newId = res?.crudeDetailId;
        setCrudeDetailId(newId);
        if (mode === 'edit') {
          // Server auto-cloned this assay's existing docs into staging — read
          // them back so Tab 2/3 show them as removable 'saved' rows.
          const draft = await getUpdateDetailDraft(newId);
          setDocumentsByEval(
            hydrateDocumentsFromDraft(draft?.pendingDocuments)
          );
        }
        // Add mode: a fresh draft has no docs → leave documentsByEval = {} (no
        // AA3 round-trip needed).
      } else {
        const update =
          mode === 'edit' ? updateUpdateDetailDraft : updateAddDetailDraft;
        await update({ ...payload, crudeDetailId });
      }
      goTo(2);
    } catch (error) {
      handleApiError(
        error,
        mode === 'edit'
          ? 'Failed to save update draft'
          : 'Failed to save assay draft'
      );
    } finally {
      setSavingDraft(false);
    }
  };

  // Shared document add — stages the file locally as a 'pending' row (no BE call).
  // The upload happens later in flushPendingDocuments() at the tab boundary.
  const handleAddDocument = ({ file, evaluationId, refDocument, effDate }) => {
    if (!crudeDetailId) {
      toast.error('Please complete Crude Info first');
      return;
    }
    setDocumentsByEval(prev => ({
      ...prev,
      [evaluationId]: [
        ...(prev[evaluationId] || []),
        {
          localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          status: 'pending',
          file,
          fileName: file.name,
          refDocument,
          effDate,
        },
      ],
    }));
  };

  // Batch-flush every 'pending' document to the Draft staging endpoint. Uploads
  // sequentially; each success promotes the row in place to 'saved' (gains
  // pendingDocumentId, drops the raw file). First failure stops and returns false,
  // leaving saved rows saved and the rest pending — the caller aborts the
  // transition and the user stays on the tab to retry.
  const flushPendingDocuments = async () => {
    if (!crudeDetailId) {
      toast.error('Please complete Crude Info first');
      return false;
    }
    const pending = [];
    Object.entries(documentsByEval).forEach(([evaluationId, rows]) => {
      (rows || []).forEach(row => {
        if (row.status === 'pending') pending.push({ evaluationId, row });
      });
    });
    if (pending.length === 0) return true;

    setFlushing(true);
    try {
      for (const { evaluationId, row } of pending) {
        try {
          const res = await uploadDraftDocument({
            file: row.file,
            crudeDetailId,
            evaluationId,
            refDocument: row.refDocument,
            effDate: row.effDate,
          });
          setDocumentsByEval(prev => ({
            ...prev,
            [evaluationId]: (prev[evaluationId] || []).map(d =>
              d.localId === row.localId
                ? {
                    localId: d.localId,
                    status: 'saved',
                    pendingDocumentId: res?.pendingDocumentId,
                    fileName: d.fileName,
                    refDocument: d.refDocument,
                    effDate: d.effDate,
                  }
                : d
            ),
          }));
        } catch (error) {
          toast.error(`Failed to upload "${row.fileName}": ${error}`);
          return false;
        }
      }
      return true;
    } finally {
      setFlushing(false);
    }
  };

  // Shared document delete. Pending (un-flushed) rows are removed locally with no
  // BE call; saved rows call the Draft delete endpoint first. In edit mode the
  // cloned existing docs are 'saved' rows — deleting one removes it from the draft
  // staging only; the original assay document is unaffected until approve.
  const handleDeleteDocument = async (evaluationId, row) => {
    if (row.status === 'pending') {
      setDocumentsByEval(prev => ({
        ...prev,
        [evaluationId]: (prev[evaluationId] || []).filter(
          d => d.localId !== row.localId
        ),
      }));
      return;
    }
    setSavingDoc(true);
    try {
      await deleteDraftDocument(row.pendingDocumentId);
      setDocumentsByEval(prev => ({
        ...prev,
        [evaluationId]: (prev[evaluationId] || []).filter(
          d => d.pendingDocumentId !== row.pendingDocumentId
        ),
      }));
      toast.success('Document removed');
    } catch (error) {
      toast.error(`Failed to remove document: ${error}`);
    } finally {
      setSavingDoc(false);
    }
  };

  // Tab 2 Next — flush staged Tab-2 files before advancing; on partial failure
  // the user stays on Tab 2 to retry.
  const handleNextFromEvaluation = async () => {
    if (await flushPendingDocuments()) goTo(3);
  };

  // Tab 3 Submit — validate implementations, flush any pending docs, then submit
  // the draft (AA6/UA6). AA6/UA6 carry no masaBerlaku/notes/description
  // override, so any edits made on Tab 3 (masaBerlaku, Notes) only reach the
  // backend if the draft itself (AA2/UA2) is re-synced with the latest `form`
  // first — re-push it here before Submit so Tab-3 edits aren't silently lost.
  const handleSubmit = async () => {
    const { errors: errorList, fields } = validateImplementation(form);
    setImplErrors(fields);
    if (errorList.length > 0) {
      warnToast(errorList[0]);
      return;
    }
    if (!crudeDetailId) {
      toast.error('Draft not created — please complete Crude Info first');
      return;
    }
    if (!(await flushPendingDocuments())) return;

    setSubmitting(true);
    try {
      const payload = buildAssayDraftPayload(
        form,
        mode,
        crude?.id,
        initialDetail?.id
      );
      const update =
        mode === 'edit' ? updateUpdateDetailDraft : updateAddDetailDraft;
      await update({ ...payload, crudeDetailId });

      const submit =
        mode === 'edit' ? submitUpdateDetailDraft : submitAddDetailDraft;
      await submit({
        crudeDetailId,
        implementations: buildImplementations(form),
      });
      toast.success(
        mode === 'edit'
          ? 'Assay updated — submitted for review'
          : 'Assay added — submitted for review'
      );
      onSaved();
    } catch (error) {
      handleApiError(error, 'Failed to submit assay');
    } finally {
      setSubmitting(false);
    }
  };

  const isTabDone = tabId => tabId < activeTab;

  return (
    <Card className='border border-primary'>
      <CardHeader className='d-flex align-items-center'>
        <h5 className='card-title mb-0 flex-grow-1'>
          {mode === 'edit' ? 'Edit Assay' : 'Add Assay'}
        </h5>
        <ActionButton
          variant='icon'
          size='sm'
          icon='ri-close-line'
          label='Close'
          onClick={onClose}
        />
      </CardHeader>
      <CardBody>
        {/* Last Update checkbox (rule §Detail 8.1 / §9). */}
        <div className='form-check mb-3'>
          <Input
            type='checkbox'
            id='assayIsLastUpdate'
            checked={!!form.isLatUpdate}
            onChange={e =>
              setForm(prev => ({ ...prev, isLatUpdate: e.target.checked }))
            }
          />
          <Label className='form-check-label' for='assayIsLastUpdate'>
            Last Update
          </Label>
        </div>

        <Nav tabs className='nav-tabs-custom nav-primary mb-3'>
          {TAB_CONFIG.map(tab => (
            <NavItem key={tab.id}>
              <NavLink
                className={classnames({
                  active: activeTab === tab.id,
                  done: isTabDone(tab.id),
                })}
                onClick={() => {
                  if (tab.id < activeTab) goTo(tab.id);
                }}
                style={{ cursor: tab.id < activeTab ? 'pointer' : 'default' }}
              >
                <i className={`${tab.icon} align-middle me-1`}></i>
                <span className='fw-semibold'>{tab.label}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId={1}>
            {activeTab === 1 && (
              <CrudeInfoTab
                form={form}
                setForm={setForm}
                errors={errors}
                onNext={handleNextFromCrudeInfo}
                saving={savingDraft}
                hideGeneral
              />
            )}
          </TabPane>
          <TabPane tabId={2}>
            {activeTab === 2 && (
              <EvaluationTab
                form={form}
                setForm={setForm}
                documentsByEval={documentsByEval}
                onAddDocument={handleAddDocument}
                onDeleteDocument={handleDeleteDocument}
                onNext={handleNextFromEvaluation}
                onBack={() => goTo(1)}
                savingDoc={savingDoc}
                flushing={flushing}
              />
            )}
          </TabPane>
          <TabPane tabId={3}>
            {activeTab === 3 && (
              <ImplementationTab
                form={form}
                setForm={setForm}
                documentsByEval={documentsByEval}
                onAddDocument={handleAddDocument}
                onDeleteDocument={handleDeleteDocument}
                onSubmit={handleSubmit}
                onBack={() => goTo(2)}
                savingDoc={savingDoc}
                flushing={flushing}
                submitting={submitting}
                implErrors={implErrors}
                setImplErrors={setImplErrors}
              />
            )}
          </TabPane>
        </TabContent>
      </CardBody>
    </Card>
  );
};

AssayEditorSection.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  crude: PropTypes.object.isRequired,
  initialDetail: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default AssayEditorSection;
